import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Shield, Clock, Users, Check } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import Header from '../components/Header';
import CryptoTicker from '../components/CryptoTicker';
import Footer from '../components/Footer';
import { flagUrl } from '../utils/currencies';
import { FadeUp, StaggerContainer, StaggerItem } from '../components/Motion';
import toast from 'react-hot-toast';
import api from '../utils/api';

const COUNTRIES = [
  { code: 'FR', name: 'France', currency: 'EUR', symbol: '€' },
  { code: 'DE', name: 'Allemagne', currency: 'EUR', symbol: '€' },
  { code: 'ES', name: 'Espagne', currency: 'EUR', symbol: '€' },
  { code: 'IT', name: 'Italie', currency: 'EUR', symbol: '€' },
  { code: 'BE', name: 'Belgique', currency: 'EUR', symbol: '€' },
  { code: 'PT', name: 'Portugal', currency: 'EUR', symbol: '€' },
  { code: 'NL', name: 'Pays-Bas', currency: 'EUR', symbol: '€' },
  { code: 'CH', name: 'Suisse', currency: 'CHF', symbol: 'CHF' },
  { code: 'GB', name: 'Royaume-Uni', currency: 'GBP', symbol: '£' },
  { code: 'RU', name: 'Russie', currency: 'RUB', symbol: '₽' },
  { code: 'CN', name: 'Chine', currency: 'CNY', symbol: '¥' },
  { code: 'JP', name: 'Japon', currency: 'JPY', symbol: '¥' },
  { code: 'KR', name: 'Corée du Sud', currency: 'KRW', symbol: '₩' },
  { code: 'IN', name: 'Inde', currency: 'INR', symbol: '₹' },
  { code: 'TH', name: 'Thaïlande', currency: 'THB', symbol: '฿' },
  { code: 'VN', name: 'Vietnam', currency: 'VND', symbol: '₫' },
  { code: 'PH', name: 'Philippines', currency: 'PHP', symbol: '₱' },
  { code: 'US', name: 'États-Unis', currency: 'USD', symbol: '$' },
  { code: 'CA', name: 'Canada', currency: 'CAD', symbol: '$' },
];

const EXCHANGE_RATES = {
  EUR: { EUR: 1, USD: 1.09, GBP: 0.86, CHF: 0.94, RUB: 97, CNY: 7.8, JPY: 163, KRW: 1420, INR: 91, THB: 37, VND: 27000, PHP: 61, CAD: 1.48 },
};

const stepVariants = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

const SendMoney = () => {
  const { t, language } = useLanguage();
  const countryName = (code, fallback) => {
    try { return new Intl.DisplayNames([language], { type: 'region' }).of(code) || fallback; } catch { return fallback; }
  };
  const [step, setStep] = useState(1);
  const [sendAmount, setSendAmount] = useState('500');
  const [fromCountry] = useState(COUNTRIES[0]);
  const [toCountry, setToCountry] = useState(COUNTRIES[9]);
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [senderEmail, setSenderEmail] = useState('');

  const getRate = () => {
    const rates = EXCHANGE_RATES[fromCountry.currency] || EXCHANGE_RATES.EUR;
    return rates[toCountry.currency] || 1;
  };

  const commission = parseFloat(sendAmount || 0) * 0.07;
  const netAmount = parseFloat(sendAmount || 0) - commission;
  const receivedAmount = netAmount * getRate();

  const formatAmount = (amount) => {
    if (amount >= 1000) return amount.toLocaleString('fr-FR', { maximumFractionDigits: 0 });
    return amount.toFixed(2);
  };

  const handleSubmit = async () => {
    try {
      await api.post('/transfer/send', {
        send_amount: parseFloat(sendAmount),
        from_currency: fromCountry.currency,
        to_country: toCountry.code,
        to_currency: toCountry.currency,
        received_amount: receivedAmount,
        recipient_name: recipientName,
        recipient_phone: recipientPhone,
        sender_email: senderEmail,
      });
      toast.success(t.common?.transferSuccess || 'Votre demande de transfert a été envoyée !');
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <CryptoTicker />
      <Header />

      {/* Dark hero */}
      <section className="bg-black py-16 md:py-20 px-6" data-testid="send-money-hero">
        <div className="max-w-4xl mx-auto text-center mb-4">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center space-x-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Globe className="w-4 h-4" /><span>{t.sendMoney?.badge || "Envoi d'argent international"}</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="text-white font-extrabold text-4xl md:text-6xl tracking-[-0.02em] mb-4" style={{ fontFamily: 'Manrope' }}>
            {t.sendMoney?.title || "Envoyez de l'argent simplement"}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="text-lg text-white/50 max-w-2xl mx-auto">
            {t.sendMoney?.subtitle || "Transférez de l'argent vers l'Europe, l'Asie et la Russie. Rapide, sécurisé et transparent."}
          </motion.p>
          <div className="flex items-center justify-center space-x-2 mt-8">
            {['fr', 'de', 'gb', 'ch', 'ru', 'cn', 'jp', 'in', 'us'].map((c, i) => (
              <motion.img key={c} src={flagUrl(c)} alt={c}
                initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 + i * 0.05 }}
                className="w-9 h-9 rounded-full border-2 border-white/20" />
            ))}
          </div>
        </div>
      </section>

      {/* Transfer Card */}
      <section className="px-6 -mt-2 pb-16 bg-gradient-to-b from-black via-black to-white">
        <div className="max-w-xl mx-auto pt-8">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="bg-white rounded-[28px] p-8 shadow-[0_24px_80px_rgb(0,0,0,0.3)]" data-testid="transfer-card">
            {/* Steps indicator */}
            <div className="flex items-center justify-center space-x-4 mb-8">
              {[1, 2, 3].map(s => (
                <div key={s} className="flex items-center">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${step >= s ? 'bg-[#191C1F] text-white' : 'bg-gray-100 text-gray-400'}`}>
                    {step > s ? <Check className="w-4 h-4" /> : s}
                  </div>
                  {s < 3 && <div className={`w-12 h-0.5 mx-2 transition-colors ${step > s ? 'bg-[#191C1F]' : 'bg-gray-200'}`}></div>}
                </div>
              ))}
            </div>

            <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} data-testid="step-1">
                <h3 className="text-xl font-extrabold text-gray-900 mb-6 tracking-tight" style={{ fontFamily: 'Manrope' }}>{t.sendMoney?.step1 || 'Combien envoyez-vous ?'}</h3>

                <div className="mb-4">
                  <label className="text-sm text-gray-500 mb-2 block font-semibold">{t.sendMoney?.youSend || 'Vous envoyez'}</label>
                  <div className="flex items-center bg-[#F5F6F7] rounded-2xl p-2 pl-5">
                    <input type="number" value={sendAmount} onChange={(e) => setSendAmount(e.target.value)}
                      className="flex-1 bg-transparent text-3xl font-bold text-gray-900 py-3 outline-none font-mono-data w-full min-w-0"
                      placeholder="500" data-testid="send-amount-input" />
                    <div className="flex items-center space-x-2 bg-white rounded-full px-4 py-2.5 border border-gray-200">
                      <img src={flagUrl('european_union')} alt="EUR" className="w-5 h-5 rounded-full" />
                      <span className="font-bold text-gray-900 text-sm">{fromCountry.currency}</span>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="text-sm text-gray-500 mb-2 block font-semibold">{t.sendMoney?.destination || 'Destination'}</label>
                  <div className="relative">
                    <img src={flagUrl(toCountry.code)} alt={toCountry.code} className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full pointer-events-none" />
                    <select value={toCountry.code} onChange={(e) => setToCountry(COUNTRIES.find(c => c.code === e.target.value))}
                      className="w-full bg-[#F5F6F7] rounded-2xl pl-12 pr-5 py-4 text-gray-900 font-semibold outline-none appearance-none cursor-pointer border border-transparent"
                      data-testid="destination-select">
                      {COUNTRIES.map(c => <option key={c.code} value={c.code}>{countryName(c.code, c.name)} ({c.currency})</option>)}
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="text-sm text-gray-500 mb-2 block font-semibold">{t.sendMoney?.theyReceive || 'Le destinataire reçoit'}</label>
                  <div className="gradient-blue rounded-2xl px-5 py-4 text-white">
                    <div className="flex items-center justify-between">
                      <span className="text-3xl font-bold font-mono-data" data-testid="received-amount">{formatAmount(receivedAmount)}</span>
                      <div className="flex items-center space-x-2">
                        <img src={flagUrl(toCountry.code)} alt={toCountry.code} className="w-6 h-6 rounded-full" />
                        <span className="font-bold">{toCountry.currency}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#F5F6F7] rounded-2xl p-4 mb-6 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">{t.sendMoney?.commission || 'Commission'} (7%)</span><span className="text-gray-700 font-mono-data">{commission.toFixed(2)} {fromCountry.currency}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">{t.sendMoney?.rate || 'Taux de change'}</span><span className="text-gray-700 font-mono-data">1 {fromCountry.currency} = {getRate().toFixed(2)} {toCountry.currency}</span></div>
                  <div className="flex justify-between font-bold border-t border-gray-200 pt-2"><span className="text-gray-700">Total</span><span className="text-gray-900 font-mono-data">{sendAmount} {fromCountry.currency}</span></div>
                </div>

                <button onClick={() => { if (parseFloat(sendAmount) <= 0 || !sendAmount) { toast.error(t.common?.invalidAmount || 'Montant invalide'); return; } setStep(2); }}
                  className="w-full btn-pill-dark py-4 text-base" data-testid="step1-next-btn">
                  {t.sendMoney?.continue || 'Continuer'}
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} data-testid="step-2">
                <h3 className="text-xl font-extrabold text-gray-900 mb-6 tracking-tight" style={{ fontFamily: 'Manrope' }}>{t.sendMoney?.step2 || 'Destinataire'}</h3>
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-sm text-gray-500 mb-2 block font-semibold">{t.sendMoney?.recipientName || 'Nom complet du destinataire'}</label>
                    <input type="text" value={recipientName} onChange={(e) => setRecipientName(e.target.value)}
                      className="w-full bg-[#F5F6F7] rounded-2xl px-5 py-4 text-gray-900 outline-none border border-transparent"
                      placeholder="Jean Dupont" data-testid="recipient-name-input" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 mb-2 block font-semibold">{t.sendMoney?.recipientPhone || 'Téléphone du destinataire'}</label>
                    <input type="tel" value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)}
                      className="w-full bg-[#F5F6F7] rounded-2xl px-5 py-4 text-gray-900 outline-none border border-transparent"
                      placeholder="+7 999 123 4567" data-testid="recipient-phone-input" />
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button onClick={() => setStep(1)} className="flex-1 btn-pill-ghost py-4 text-base" data-testid="step2-back-btn">{t.sendMoney?.back || 'Retour'}</button>
                  <button onClick={() => { if (!recipientName.trim()) { toast.error(t.common?.nameRequired || 'Nom requis'); return; } if (!recipientPhone.trim()) { toast.error(t.common?.phoneRequired || 'Téléphone requis'); return; } setStep(3); }}
                    disabled={!recipientName} className="flex-1 btn-pill-dark py-4 text-base disabled:opacity-40" data-testid="step2-next-btn">{t.sendMoney?.continue || 'Continuer'}</button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} data-testid="step-3">
                <h3 className="text-xl font-extrabold text-gray-900 mb-6 tracking-tight" style={{ fontFamily: 'Manrope' }}>{t.sendMoney?.step3 || 'Confirmation'}</h3>
                <div className="bg-[#F5F6F7] rounded-2xl p-6 mb-6 space-y-3">
                  <div className="flex justify-between"><span className="text-gray-500">{t.sendMoney?.youSend || 'Vous envoyez'}</span><span className="font-bold text-gray-900 font-mono-data">{sendAmount} {fromCountry.currency}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">{t.sendMoney?.theyReceive || 'Le destinataire reçoit'}</span><span className="font-bold text-emerald-600 font-mono-data">{formatAmount(receivedAmount)} {toCountry.currency}</span></div>
                  <div className="flex justify-between items-center"><span className="text-gray-500">{t.sendMoney?.to || 'Vers'}</span>
                    <span className="font-bold text-gray-900 flex items-center space-x-2"><img src={flagUrl(toCountry.code)} alt="" className="w-5 h-5 rounded-full" /><span>{countryName(toCountry.code, toCountry.name)}</span></span>
                  </div>
                  <div className="flex justify-between"><span className="text-gray-500">{t.sendMoney?.recipient || 'Destinataire'}</span><span className="font-bold text-gray-900">{recipientName}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">{t.sendMoney?.commission || 'Commission'}</span><span className="text-gray-700 font-mono-data">{commission.toFixed(2)} {fromCountry.currency}</span></div>
                </div>
                <div className="mb-6">
                  <label className="text-sm text-gray-500 mb-2 block font-semibold">{t.sendMoney?.yourEmail || 'Votre email'}</label>
                  <input type="email" value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)}
                    className="w-full bg-[#F5F6F7] rounded-2xl px-5 py-4 text-gray-900 outline-none border border-transparent"
                    placeholder="votre@email.com" data-testid="sender-email-input" />
                </div>
                <div className="flex space-x-3">
                  <button onClick={() => setStep(2)} className="flex-1 btn-pill-ghost py-4 text-base" data-testid="step3-back-btn">{t.sendMoney?.back || 'Retour'}</button>
                  <button onClick={() => { if (!senderEmail || !senderEmail.includes('@')) { toast.error(t.common?.invalidEmail || 'Email invalide'); return; } handleSubmit(); }}
                    disabled={!senderEmail} className="flex-1 btn-pill-dark py-4 text-base disabled:opacity-40" data-testid="confirm-send-btn">
                    {t.sendMoney?.confirmSend || "Confirmer l'envoi"}
                  </button>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="s4" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8" data-testid="step-4">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-extrabold text-gray-900 mb-2 tracking-tight" style={{ fontFamily: 'Manrope' }}>{t.sendMoney?.success || 'Transfert initié !'}</h3>
                <p className="text-gray-500 mb-6">{t.sendMoney?.successDesc || 'Votre demande de transfert a été envoyée. Vous recevrez un email avec les instructions de paiement.'}</p>
                <Link to="/"><button className="btn-pill-dark px-8 py-3" data-testid="back-home-btn">{t.sendMoney?.backHome || "Retour à l'accueil"}</button></Link>
              </motion.div>
            )}
            </AnimatePresence>
          </motion.div>

          <div className="flex items-center justify-center space-x-8 mt-8 text-sm text-gray-400">
            <div className="flex items-center space-x-2"><Shield className="w-4 h-4" /><span>{t.sendMoney?.secure || 'Transfert sécurisé'}</span></div>
            <div className="flex items-center space-x-2"><Clock className="w-4 h-4" /><span>{t.sendMoney?.fast || '24-48h'}</span></div>
            <div className="flex items-center space-x-2"><Users className="w-4 h-4" /><span>50K+ {t.sendMoney?.users || 'utilisateurs'}</span></div>
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section className="py-20 px-6 bg-white" data-testid="destinations-section">
        <div className="max-w-4xl mx-auto">
          <FadeUp>
            <h2 className="font-extrabold text-3xl md:text-4xl text-gray-900 mb-10 text-center tracking-tight" style={{ fontFamily: 'Manrope' }}>{t.sendMoney?.destinations || 'Destinations populaires'}</h2>
          </FadeUp>
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {COUNTRIES.filter(c => ['RU', 'CN', 'JP', 'IN', 'DE', 'GB', 'TH', 'US'].includes(c.code)).map(country => (
              <StaggerItem key={country.code}>
                <button onClick={() => { setToCountry(country); setStep(1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="w-full bg-[#F5F6F7] rounded-3xl p-6 hover:bg-gray-100 hover:-translate-y-1 transition-all duration-200 text-center"
                  data-testid={`dest-${country.code}`}>
                  <img src={flagUrl(country.code)} alt={country.name} className="w-12 h-12 rounded-full mx-auto mb-3" />
                  <span className="text-sm font-bold text-gray-900 block">{countryName(country.code, country.name)}</span>
                  <div className="text-xs text-gray-400 mt-1">{country.currency}</div>
                </button>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SendMoney;
